/** Exact decimal arithmetic for money and quantities.
 *
 * The API sends money as JSON decimal numbers and the backend computes with BigDecimal, so
 * the client must not compute with binary floats: 1.005 * 100 is 100.49999… in IEEE 754 and
 * would round the wrong way. A value here is an integer `units` scaled by 10^-scale, built
 * from the number's shortest decimal string (which is exactly what the server serialised).
 *
 * Rounding is HALF_UP (BigDecimal's usual money mode). That is an assumption until confirmed
 * against the real backend (plan: "Risks"); it is isolated in `round()`. */

export type Dec = { readonly units: bigint; readonly scale: number };

const TEN = 10n;
const pow10 = (n: number): bigint => TEN ** BigInt(n);

export function dec(value: number | string | Dec): Dec {
  if (typeof value === "object") return value;
  if (typeof value === "number") {
    if (!Number.isFinite(value)) throw new RangeError(`Not a finite amount: ${value}`);
    // String() gives the shortest round-tripping form; exponent forms (1e-7, 1e21) are rare
    // for money but are expanded rather than mis-parsed.
    value = /e/i.test(String(value)) ? expandExponent(value) : String(value);
  }
  const s = value.trim();
  const m = /^([+-])?(\d*)(?:\.(\d*))?$/.exec(s);
  if (!m || (m[2] === "" && (m[3] ?? "") === "")) throw new RangeError(`Not a decimal: "${value}"`);
  const frac = m[3] ?? "";
  const units = BigInt((m[2] || "0") + frac) * (m[1] === "-" ? -1n : 1n);
  return { units, scale: frac.length };
}

function expandExponent(n: number): string {
  // toFixed is exact enough for 20 places on values that reached us as decimals.
  return n.toFixed(20).replace(/\.?0+$/, "");
}

function align(a: Dec, b: Dec): [bigint, bigint, number] {
  const scale = Math.max(a.scale, b.scale);
  return [a.units * pow10(scale - a.scale), b.units * pow10(scale - b.scale), scale];
}

export const add = (a: Dec, b: Dec): Dec => {
  const [x, y, scale] = align(a, b);
  return { units: x + y, scale };
};
export const sub = (a: Dec, b: Dec): Dec => {
  const [x, y, scale] = align(a, b);
  return { units: x - y, scale };
};
export const mul = (a: Dec, b: Dec): Dec => ({ units: a.units * b.units, scale: a.scale + b.scale });
export const neg = (a: Dec): Dec => ({ units: -a.units, scale: a.scale });
export const cmp = (a: Dec, b: Dec): -1 | 0 | 1 => {
  const [x, y] = align(a, b);
  return x < y ? -1 : x > y ? 1 : 0;
};
export const isZero = (a: Dec): boolean => a.units === 0n;
export const sum = (xs: readonly Dec[]): Dec => xs.reduce(add, ZERO);
export const min = (a: Dec, b: Dec): Dec => (cmp(a, b) <= 0 ? a : b);
export const max = (a: Dec, b: Dec): Dec => (cmp(a, b) >= 0 ? a : b);
export const ZERO: Dec = { units: 0n, scale: 0 };

/** Divide a bigint, rounding HALF_UP (away from zero on exactly .5). */
function divHalfUp(n: bigint, d: bigint): bigint {
  if (d === 0n) throw new RangeError("Division by zero");
  const negative = n < 0n !== d < 0n;
  const an = n < 0n ? -n : n;
  const ad = d < 0n ? -d : d;
  const q = an / ad;
  const r = an % ad;
  const rounded = r * 2n >= ad ? q + 1n : q;
  return negative ? -rounded : rounded;
}

/** Round to `scale` places, HALF_UP. */
export function round(a: Dec, scale = 2): Dec {
  if (a.scale <= scale) return { units: a.units * pow10(scale - a.scale), scale };
  return { units: divHalfUp(a.units, pow10(a.scale - scale)), scale };
}

/** a ÷ b, rounded HALF_UP to `scale` places. */
export function div(a: Dec, b: Dec, scale = 2): Dec {
  if (b.units === 0n) throw new RangeError("Division by zero");
  // a/b = (a.units / 10^as) / (b.units / 10^bs) = a.units·10^(bs+scale−as) / b.units · 10^−scale
  const shift = b.scale + scale - a.scale;
  const n = shift >= 0 ? a.units * pow10(shift) : a.units;
  const d = shift >= 0 ? b.units : b.units * pow10(-shift);
  return { units: divHalfUp(n, d), scale };
}

/** Floor toward −∞ to an integer (points earned = floor(total ÷ 100)). */
export function floorInt(a: Dec): bigint {
  const d = pow10(a.scale);
  const q = a.units / d;
  return a.units < 0n && a.units % d !== 0n ? q - 1n : q;
}

/** Back to a JS number for JSON payloads and display. Exact for any 2-dp amount below 2^53 cents. */
export function toNumber(a: Dec): number {
  return Number(toFixed(a, a.scale));
}

/** Plain decimal string with exactly `places` digits (rounds HALF_UP when needed). */
export function toFixed(a: Dec, places = 2): string {
  const r = round(a, places);
  const negative = r.units < 0n;
  const digits = (negative ? -r.units : r.units).toString().padStart(places + 1, "0");
  const int = places ? digits.slice(0, -places) : digits;
  const frac = places ? `.${digits.slice(-places)}` : "";
  return `${negative ? "-" : ""}${int}${frac}`;
}

/** Round a plain number to 2 dp exactly (HALF_UP), returning a number. */
export const round2 = (n: number): number => toNumber(round(dec(n), 2));
